import React, { useState } from "react";
import "i18next";
import { useTranslation } from "react-i18next";
import Slide from "@mui/material/Slide";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  styled,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "0.10em",
  fontSize: "12px",
}));

const DialogBox = ({
  isOpen,
  action,
  cancelAction,
  title,
  message,
  messageType,
  itemList,
  isConfirmation,
  sx = {},
}) => {
  const { t } = useTranslation(["DialogBox"], {
    useSuspense: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const [dialogConfirmAction, setDialogConfirmAction] = useState(null);
  const [dialogCancelAction, setDialogCancelAction] = useState(null);

  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogBulletList, setDialogBulletList] = useState([]);
  const [isConfirmationDialog, setIsConfirmationDialog] = useState(true);

  React.useEffect(() => {
    const setDialogBox = () => {
      if (action) {
        setDialogConfirmAction(() => action);
      }
      if (cancelAction) {
        setDialogCancelAction(() => cancelAction);
      }
      setDialogTitle(title || t("default_dialog.title"));
      setDialogMessage(message || t("default_dialog.message"));
      setDialogBulletList(itemList || []);
      setIsConfirmationDialog(isConfirmation || false);
      setDialogOpen(isOpen || false);
    };

    setDialogBox();
  }, [isOpen, action, cancelAction, title, message, itemList, isConfirmation]);

  const handleDialogClose = (confirm) => {
    setDialogOpen(false);
    if (confirm && dialogConfirmAction) {
      dialogConfirmAction();
    } else if (!confirm && dialogCancelAction) {
      dialogCancelAction();
    }
  };

  const getTitleColor = () => {
    switch (messageType) {
      case "Information":
        return "#0288d1";
      case "Confirmation":
        return "#ed6c02";
      case "Warning":
        return "#d32f2f";
      default:
        return "inherit";
    }
  };

  const getIcon = () => {
    switch (messageType) {
      case "Information":
        return <InfoIcon sx={{ width: 60, height: 60, marginBottom: 1 }} />;
      case "Confirmation":
        return <WarningIcon sx={{ width: 60, height: 60, marginBottom: 1 }} />;
      case "Warning":
        return <ErrorIcon sx={{ width: 60, height: 60, marginBottom: 1 }} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => handleDialogClose(false)}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      maxWidth="md"
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        ...sx,
      }}
    >
      <DialogTitle
        id="alert-dialog-slide-title"
        sx={{
          fontFamily: "Urbanist, Arial, sans-serif",
          fontSize: "1.5rem",
          fontWeight: "bold",
          letterSpacing: "0.10em",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: getTitleColor(),
        }}
      >
        {getIcon()}
        {dialogTitle}
      </DialogTitle>
      <DialogContent
        sx={{
          textAlign: "center",
          "& ul": {
            textAlign: "left",
          },
        }}
      >
        <DialogContentText
          id="alert-dialog-slide-description"
          sx={{
            fontFamily: "Urbanist, Arial, sans-serif",
            fontSize: "1.2rem",
            letterSpacing: "0.10em",
          }}
        >
          {dialogMessage}
          {dialogBulletList.length > 0 && (
            <>
              <Box sx={{ marginTop: "16px" }} />
              <ul>
                {dialogBulletList.map((message, index) => (
                  <li
                    key={`${message}-${index}`}
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                ))}
              </ul>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          "& > :not(:last-of-type)": {
            marginRight: "16px",
          },
          paddingBottom: "24px",
        }}
      >
        <CustomButton
          variant="outlined"
          onClick={() => handleDialogClose(false)}
        >
          {isConfirmationDialog
            ? t("dialog_box.button02")
            : t("dialog_box.button04")}
        </CustomButton>
        <CustomButton
          variant="contained"
          sx={{ backgroundColor: "#3299FF" }}
          onClick={() => handleDialogClose(true)}
          autoFocus
        >
          {isConfirmationDialog
            ? t("dialog_box.button01")
            : t("dialog_box.button03")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

DialogBox.propTypes = {
  isOpen: PropTypes.bool,
  action: PropTypes.func, // Function to execute on confirmation
  cancelAction: PropTypes.func, // Function to execute on cancellation
  title: PropTypes.string, // Title of the dialog
  message: PropTypes.string, // Main message in the dialog
  messageType: PropTypes.oneOf(["Confirmation", "Information", "Warning"]), // Prop type for messageType
  itemList: PropTypes.arrayOf(PropTypes.string), // Array of bullet points (optional)
  isConfirmation: PropTypes.bool, // If true, show "Yes/No" buttons, otherwise "Proceed/Cancel"
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]), // Style overrides for the Dialog component
};

export default DialogBox;
